import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { computeCarrierLineItemsTotal } from '@common/order/validations';
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { createAppStore } from '@store/createAppStore';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { getMaxRateRequestsData } from '@store/Global/actions';
import {
  applyOrderHydration,
  validateFieldForOrder,
} from '@store/orders/actions/orderFormActions';
import {
  approveRateRequest,
  denyRateRequest,
  requestMaxRateIncrease,
} from '@store/orders/services/rateRequestServices';
import { orderStore, setOrderStore } from '@store/orders/store';
import { userStore } from '@store/user/store';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import type {
  MaxRateRequestFilter,
  MaxRateRequestGroup,
  MaxRateRequestRow,
  RateRequest,
  RateRequestCharge,
} from '@typeDefinitions/rateRequestTypes';
import { handleToast } from '@utils/utils';
import { DateTime } from 'luxon';

type RateRequestState = {
  submittingLoadIds: number[];
};

const [rateRequestStore, setRateRequestStore] =
  createAppStore<RateRequestState>(
    { submittingLoadIds: [] },
    'rateRequestStore',
  );

const changeTrackingSource =
  CHANGE_TRACKING_SOURCES.orderDetails.carrierBilling;

const toCentsRoundedUp = (amount: number) =>
  Math.ceil(Number((amount * 100).toFixed(6))) / 100;

const setSubmitting = (loadId: number, submitting: boolean) =>
  setRateRequestStore('submittingLoadIds', (ids) =>
    submitting
      ? [...ids.filter((id) => id !== loadId), loadId]
      : ids.filter((id) => id !== loadId),
  );

export const isMaxRateRequestEnabled = () =>
  isFeatureFlagEnabled(FeatureFlags.MaxRateOverrideApprovalRequest)();

const hasMaxRateApprovalPermission = () =>
  isFeatureFlagEnabled(FeatureFlags.PermissionCarrierPayMaxRateApproval)();

export const canViewMaxRateApprovals = () =>
  isMaxRateRequestEnabled() && hasMaxRateApprovalPermission();

const refreshMaxRateRequests = () => {
  if (canViewMaxRateApprovals()) {
    void getMaxRateRequestsData();
  }
};

export const getPendingRateRequest = (loadId: number) => {
  const request =
    orderStore.order.loads.find((load) => load.id === loadId)
      ?.pendingRateRequest ?? null;
  // Ignore unsaved loads and a request for another load (e.g. a copied order).
  return loadId > 0 && request?.loadId === loadId ? request : null;
};

export const isRateRequestSubmitting = (loadId: number) =>
  rateRequestStore.submittingLoadIds.includes(loadId);

export const canDecideRateRequest = (
  request: Pick<RateRequest, 'requestedBy'>,
) =>
  request.requestedBy !== userStore.user.id && hasMaxRateApprovalPermission();

export const buildRateRequestCharges = (
  lineItems: ILoadEditViewModel['lineItems'],
): Omit<RateRequestCharge, 'id'>[] =>
  (lineItems ?? [])
    .filter((item) => item.operationType !== 'Delete')
    .map((item) => ({
      type: item.type ?? '',
      description: item.description ?? '',
      quantity: Number(item.quantity),
      rate: Number(item.rate),
    }));

export const getRateRequesterName = (request: RateRequest) => {
  if (request.requestedByName) {
    return request.requestedByName;
  }
  return request.requestedBy === userStore.user.id
    ? `${userStore.user.firstName} ${userStore.user.lastName}`
    : 'another user';
};

export const formatRateRequestDate = (date: string) =>
  DateTime.fromISO(date, { zone: 'utc' }).toLocal().toFormat('M/d/yy, h:mm a');

// Saved by the API, so the change isn't tracked as a user edit.
const updateLoadFromApi = (
  loadId: number,
  changes: Pick<ILoadEditViewModel, 'pendingRateRequest' | 'maxPrice'>,
) => {
  const index = orderStore.order.loads.findIndex((load) => load.id === loadId);
  if (index < 0) {
    return -1;
  }
  applyOrderHydration(() => {
    setOrderStore('order', 'loads', index, changes);
  });
  return index;
};

// Updates only the approved load's Max Rate, keeping the user's unsaved edits.
const applyApprovedMaxRate = (loadId: number, maxRate: number) => {
  const index = updateLoadFromApi(loadId, {
    maxPrice: maxRate,
    pendingRateRequest: null,
  });
  if (index < 0) {
    return;
  }
  void validateFieldForOrder(
    `loads.${index}.maxRateError`,
    `loads[${index}].maxRateError`,
    { load: orderStore.order.loads[index] },
  );
};

export const submitMaxRateIncreaseRequest = async (
  load: ILoadEditViewModel,
) => {
  if (isRateRequestSubmitting(load.id)) {
    return;
  }
  setSubmitting(load.id, true);
  try {
    const request = await requestMaxRateIncrease(
      {
        loadId: load.id,
        maxRate: toCentsRoundedUp(computeCarrierLineItemsTotal(load.lineItems)),
        charges: buildRateRequestCharges(load.lineItems),
      },
      changeTrackingSource,
    );
    updateLoadFromApi(load.id, { pendingRateRequest: request });
    refreshMaxRateRequests();
    handleToast(ToastType.Success, 'Max rate increase requested');
  } catch (error: unknown) {
    handleToast(ToastType.Error, (error as Error).message);
  } finally {
    setSubmitting(load.id, false);
  }
};

export const decideRateRequest = async (
  request: Pick<RateRequest, 'id' | 'loadId'>,
  approved: boolean,
  notes?: string,
) => {
  if (isRateRequestSubmitting(request.loadId)) {
    return false;
  }
  setSubmitting(request.loadId, true);
  try {
    const decide = approved ? approveRateRequest : denyRateRequest;
    const decided = await decide(
      {
        rateRequestId: request.id,
        notes: notes?.trim() ? notes.trim() : undefined,
      },
      changeTrackingSource,
    );
    if (approved) {
      applyApprovedMaxRate(request.loadId, decided.maxRate);
    } else {
      updateLoadFromApi(request.loadId, { pendingRateRequest: null });
    }
    refreshMaxRateRequests();
    handleToast(
      ToastType.Success,
      approved ? 'Max rate increase approved' : 'Max rate increase denied',
    );
    return true;
  } catch (error: unknown) {
    handleToast(ToastType.Error, (error as Error).message);
    return false;
  } finally {
    setSubmitting(request.loadId, false);
  }
};

export const groupMaxRateRequests = (
  rows: MaxRateRequestRow[],
): MaxRateRequestGroup[] => {
  const groups = new Map<number, MaxRateRequestGroup>();
  rows.forEach((row) => {
    const group = groups.get(row.id) ?? {
      id: row.id,
      loadId: row.loadId,
      orderId: row.orderId,
      loadNumber: row.loadNumber,
      currentMaxRate: row.currentMaxRate,
      requestedById: row.requestedById,
      requestedByName: row.requestedByName,
      requestedMaxRate: row.requestedMaxRate,
      requestedDate: row.requestedDate,
      requestStatus: row.requestStatus,
      actionTakenById: row.actionTakenById,
      actionTakenByName: row.actionTakenByName,
      actionTakenOnDate: row.actionTakenOnDate,
      notes: row.notes,
      lineItems: [],
    };
    if (row.lineItemId !== null && row.lineItemId > 0) {
      group.lineItems.push({
        type: row.lineItemType ?? '',
        amount: row.lineItemAmount ?? 0,
        quantity: row.lineItemQuantity ?? 0,
      });
    }
    groups.set(row.id, group);
  });
  return [...groups.values()];
};

export const filterMaxRateRequests = (
  requests: MaxRateRequestGroup[],
  filter: MaxRateRequestFilter,
) => {
  if (filter === 'All') {
    return requests;
  }
  return requests.filter((request) =>
    filter === 'Pending'
      ? request.requestStatus === 'Pending'
      : request.requestStatus !== 'Pending',
  );
};

export const getPendingMaxRateRequestCount = (rows: MaxRateRequestRow[]) =>
  new Set(
    rows.filter((row) => row.requestStatus === 'Pending').map((row) => row.id),
  ).size;
